import Header from '@components/Shared/Layout/Header';
// import AuthenticationMethodsCard from '@components/ServerConfig/AuthenticationMethodsCard';
import CustomizationLocalesCard from '@components/ServerConfig/CustomizationLocalesCard';
import DevicesCard from '@components/ServerConfig/DevicesCard';
import ServerStatusCard from '@components/ServerConfig/ServerStatusCard';
import SessionExpirationCard from '@components/ServerConfig/SessionExpirationCard';
import SsoCard from '@components/ServerConfig/SsoCard';
import { useLocalization } from '@lib/localization';
import { Box, Button } from '@mui/material';
import { type AllowedMethods, useGetConfigQuery, useUpdateConfigMutation } from '@store/api/rootEndpoints';
import { useLoading, usePermission } from '@store/hooks';
import { useEffect, useId, useState } from 'react';
import { CheckIcon } from 'react-line-awesome';

const ServerConfigPage = () => {
  const [sessionExpiresInSeconds, setSessionExpiresInSeconds] = useState<number>(0);
  const [allowedMethods, setAllowedMethods] = useState<AllowedMethods[]>([]);
  // The card answers this: a session length outside the range the API accepts, or none
  // chosen yet on a server that was never configured, must not reach the PUT.
  const [isSessionValid, setIsSessionValid] = useState(false);
  const { data } = useGetConfigQuery();
  const [updateConfig, { isLoading, error }] = useUpdateConfigMutation();
  const { show, hide } = useLoading();
  const { t } = useLocalization();

  const { can } = usePermission();
  const hasUpdatePermission = can('server_config:put');
  // Admins reach this page for the session settings, but the status report is sysadmin-only.
  const canSeeServerStatus = can('health:get');
  // Reading included: the settings say who may get in and as what, so they are the sysadmin's.
  const canSeeSso = can('sso_config:get');

  const componentId = useId();

  useEffect(() => {
    (isLoading ? show : hide)(componentId);
  }, [componentId, hide, isLoading, show]);

  // Only when the stored session settings change. The languages and devices cards save their
  // own parts of the same config, and the re-read after them comes with the `auth` object the
  // cache had (it keeps what did not change), so it does not put back a session length chosen
  // here and not saved yet. Session settings that were changed elsewhere meanwhile do arrive
  // with that re-read, and take the place of what was chosen here.
  const auth = data?.auth;

  useEffect(() => {
    setSessionExpiresInSeconds(auth?.sessionExpiresInSeconds ?? 0);
    setAllowedMethods(auth?.allowedMethods ?? []);
  }, [auth, error]);

  const save = async () => {
    await updateConfig({ auth: { allowedMethods, sessionExpiresInSeconds } });
  };

  const hasChanges = !!data && (data.auth.sessionExpiresInSeconds !== sessionExpiresInSeconds
    || data.auth.allowedMethods.join() !== allowedMethods.join());

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />
      {canSeeServerStatus && <ServerStatusCard />}
      {/* These save by themselves. Over the session card, so that the page's Save stays right
          under the one card it saves. */}
      <CustomizationLocalesCard />
      <DevicesCard />
      {canSeeSso && <SsoCard />}
      <SessionExpirationCard
        value={sessionExpiresInSeconds}
        onChanged={setSessionExpiresInSeconds}
        onValidityChanged={setIsSessionValid}
      />
      {/* <AuthenticationMethodsCard value={allowedMethods} onChanged={setAllowedMethods} /> */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          sx={{ px: 2 }}
          startIcon={<CheckIcon />}
          disabled={!hasUpdatePermission || !hasChanges || !isSessionValid || isLoading}
        >
          {t('Common_Save')}
        </Button>
      </Box>
    </Box >
  );
};

export default ServerConfigPage;
